import { FC } from "react";
import { Alert, AlertTitle, Box, Button, Chip, Typography } from "@mui/material";
import { IconTypes } from "../../components/icons.types";
import {
  PrePublishCheck,
  PrePublishFinding,
} from "../../features/catalogs/catalog.service";

/**
 * S29 Slice A — the two-door recovery UX, both variants.
 *
 * The panel appears inside the publish dialog when the pre-publish check found a BLOCKER on the
 * catalog. Two doors, always: fix it, or publish it anyway. Which door leads, and what the other
 * one costs, is the whole difference between the variants:
 *
 * - `nudge` — the defect is named, both doors are one click, and the Publish button never changes.
 *   Costs an ignored panel when we are wrong.
 * - `halt`  — Publish is withheld until the user chooses. "Publish anyway" is still there and is
 *   still one click; it just has to be *chosen* rather than defaulted into. Costs a blocked publish
 *   when we are wrong.
 *
 * Neither variant is a dead end. "Two doors, both fine" and a non-punished decline are the
 * guidance-model rules (memory/features/design-quality/guidance-model.md), and halting is not a
 * licence to break them — the halt variant makes the second door deliberate, not unavailable.
 *
 * Gated on `blockers`, never on any aggregate score. That is the rule the removed vision scorer
 * left behind: a catalog scored 64/100 while its product names were navy on a navy banner.
 */

type PrePublishCheckPanelProps = {
  check: PrePublishCheck;
  /** True once the user has chosen the "publish anyway" door (halt variant only). */
  overridden: boolean;
  onOverride: () => void;
  /** Hand the catalog back to whoever can fix it — closes the dialog. */
  onFix: () => void;
};

const FindingRow: FC<{ finding: PrePublishFinding }> = ({ finding }) => (
  <Box component="li" sx={{ mb: 1.25, "&:last-of-type": { mb: 0 } }}>
    <Typography variant="body2" component="span">
      {finding.page ? <b>Page {finding.page}: </b> : null}
      {finding.issue}
    </Typography>
    {finding.source !== "structural" && (
      <Chip
        size="small"
        label={finding.source === "render" ? "measured in the PDF" : "seen in the render"}
        variant="outlined"
        sx={{ ml: 1, height: 18, fontSize: 11 }}
      />
    )}
    {finding.fix && (
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mt: 0.25 }}
      >
        Fix: {finding.fix}
      </Typography>
    )}
  </Box>
);

export const PrePublishCheckPanel: FC<PrePublishCheckPanelProps> = ({
  check,
  overridden,
  onOverride,
  onFix,
}) => {
  // Blockers only. Warnings are real but they are not worth interrupting a publish for, and a
  // panel that fires on every catalog is a panel nobody reads.
  const blockers = check.findings.filter((f) => f.severity === "blocker");
  if (!check.enabled || blockers.length === 0) return null;

  const isHalt = check.mode === "halt";
  const plural = blockers.length === 1 ? "" : "s";

  return (
    <Alert
      severity={isHalt ? "error" : "warning"}
      icon={isHalt ? <IconTypes.Error /> : <IconTypes.VisibilityOn />}
      sx={{ mb: 2 }}
    >
      <AlertTitle>
        {isHalt
          ? `Your customers will see ${blockers.length === 1 ? "this" : "these"} — worth fixing first`
          : `Before you publish: ${blockers.length} thing${plural} to look at`}
      </AlertTitle>

      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 1.5 }}>
        {blockers.map((f, i) => (
          <FindingRow key={i} finding={f} />
        ))}
      </Box>

      {isHalt && !overridden && (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Publishing is paused until you choose. Both choices are fine.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={onFix}
              startIcon={<IconTypes.Edit />}
            >
              Let me fix it
            </Button>
            <Button size="small" variant="outlined" color="inherit" onClick={onOverride}>
              Publish anyway
            </Button>
          </Box>
        </>
      )}

      {isHalt && overridden && (
        <Typography variant="body2">
          Publishing as-is. Use <b>Publish</b> below when you are ready.
        </Typography>
      )}

      {!isHalt && (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You can publish as-is — this is a heads-up, not a stop.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={onFix}
            startIcon={<IconTypes.Edit />}
          >
            Let me fix it first
          </Button>
        </>
      )}
    </Alert>
  );
};
